import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertCircle, ClipboardCheck, Building2, FileSearch, ShieldCheck, HeartHandshake, Sparkles, ArrowRight, Star, MessageCircle, Phone, User, Send, X, Stethoscope } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { usePageTitle } from "../hooks/usePageTitle";
import { trackWhatsApp } from "../lib/beacon";
import FeaturedCarousel from "../components/home/FeaturedCarousel";
import HomeNudge from "../components/home/HomeNudge";

const SUPPORT_WA = process.env.REACT_APP_SUPPORT_WA || "+5511996420785";
const digits = (s) => (s || "").replace(/[^0-9]/g, "");

/**
 * Balão flutuante SEMPRE visível na home. Adapta a mensagem:
 *  - se algum consultor está no plantão: "Consultor RAF Renan online"
 *    e o link vai para wa.me DO consultor (state.phone).
 *  - se nenhum consultor está online: "Atendimento RAF 24h" com wa.me do
 *    número institucional (REACT_APP_SUPPORT_WA).
 */
const OnCallBadge = () => {
  const [state, setState] = useState(null);
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [errPhone, setErrPhone] = useState(false);
  const [errName, setErrName] = useState(false);
  useEffect(() => {
    let mounted = true;
    const load = () => api.get("/public/on-call")
      .then((r) => { if (mounted) setState(r.data); })
      .catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => { mounted = false; clearInterval(id); };
  }, []);
  const online = !!(state && state.online);
  const targetPhone = digits(online ? (state.phone || SUPPORT_WA) : SUPPORT_WA);
  const greetTitle = online ? "Consultor RAF online" : "Atendimento RAF 24h";
  const greetSub = online
    ? (state.first_name ? `${state.first_name} pode te atender agora` : "Atendimento disponível agora")
    : "Fale conosco no WhatsApp";
  const initials = (state?.name || state?.first_name || "").split(" ").filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const attendantFirstName = online && state?.first_name ? state.first_name : "";
  const attendantHeaderName = attendantFirstName
    ? `${attendantFirstName} · Consultor RAF Direct`
    : "Atendimento RAF Direct";

  const openModal = (e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); };
  const closeModal = () => {
    setOpen(false);
    setErrPhone(false); setErrName(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const pDigits = digits(phone);
    const nameTrim = name.trim();
    const badPhone = pDigits.length < 10 || pDigits.length > 13;
    const badName = nameTrim.length < 2;
    setErrPhone(badPhone); setErrName(badName);
    if (badPhone || badName) return;
    const attendant = attendantFirstName || "consultor RAF";
    const msg = `Olá, ${attendant}, me chamo ${nameTrim} (tel ${pDigits}) e vim através do site da RAF Direct e preciso de ajuda urgente.`;
    const url = `https://wa.me/${targetPhone}?text=${encodeURIComponent(msg)}`;
    trackWhatsApp("home_oncall_badge", { attendant, target: targetPhone });
    // Chamada síncrona dentro do click submit — preserva o user-gesture, evita popup-blocker.
    window.open(url, "_blank", "noopener");
    closeModal();
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        data-testid="on-call-badge"
        className={`fixed bottom-4 right-4 z-40 group inline-flex items-center gap-3 pl-2 pr-4 py-2 rounded-full bg-white shadow-lg border transition-all hover:shadow-xl ${online ? "border-green-200 hover:border-green-500" : "border-slate-200 hover:border-[#25D366]"}`}
      >
        {online ? (
          <span className="relative shrink-0">
            {state.avatar_url ? (
              <img src={state.avatar_url} alt={state.name || ""}
                className="w-9 h-9 rounded-full object-cover border-2 border-green-400" />
            ) : (
              <span className="w-9 h-9 rounded-full bg-green-100 border-2 border-green-400 inline-flex items-center justify-center text-[11px] font-bold text-green-700">
                {initials || "RAF"}
              </span>
            )}
            <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 border-2 border-white">
              <span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-70 animate-ping" />
            </span>
          </span>
        ) : (
          <span className="relative flex items-center justify-center w-3 h-3 ml-1">
            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#25D366]" />
          </span>
        )}
        <span className="text-left leading-tight">
          <span className={`block text-[10px] uppercase tracking-widest font-bold ${online ? "text-green-700" : "text-[#128C7E]"}`}>
            {greetTitle}
          </span>
          <span className="block text-xs text-slate-700 font-semibold">{greetSub}</span>
        </span>
        <MessageCircle className={`w-4 h-4 transition-colors ${online ? "text-green-600" : "text-[#25D366]"}`} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={closeModal}
          data-testid="wa-contact-modal"
        >
          <form
            onClick={(e) => e.stopPropagation()}
            onSubmit={handleSubmit}
            className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200"
          >
            {/* Header verde estilo WhatsApp */}
            <div className="bg-[#25D366] text-white px-5 py-4 flex items-center gap-3">
              <span className="shrink-0 w-11 h-11 rounded-full bg-white/95 flex items-center justify-center overflow-hidden">
                {online && state?.avatar_url ? (
                  <img src={state.avatar_url} alt={state?.name || ""} className="w-full h-full object-cover" />
                ) : (
                  <MessageCircle className="w-6 h-6 text-[#25D366]" strokeWidth={2.4} />
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-[15px] truncate" data-testid="wa-modal-title">{attendantHeaderName}</div>
                <div className="text-[11px] opacity-95 truncate">www.rafdirect.com.br</div>
                <div className="text-[11px] flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-300 inline-block" />
                  Online
                </div>
              </div>
              <button
                type="button"
                onClick={closeModal}
                aria-label="Fechar"
                className="shrink-0 text-white/90 hover:text-white p-1 -m-1"
                data-testid="wa-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Faixa amarela */}
            <div className="bg-amber-50 border-b border-amber-100 text-amber-900 text-sm font-semibold text-center py-2.5 px-4">
              Fale com um Consultor pelo WhatsApp
            </div>

            {/* Inputs */}
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="sr-only">DDD + Celular</span>
                <div className={`flex items-center gap-2 border rounded-full px-4 py-2.5 transition-colors ${errPhone ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50 focus-within:border-[#25D366] focus-within:bg-white"}`}>
                  <Phone className="w-4 h-4 text-[#25D366] shrink-0" />
                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="DDD + Celular *"
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value); if (errPhone) setErrPhone(false); }}
                    className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400"
                    data-testid="wa-modal-phone"
                  />
                </div>
                {errPhone && <div className="text-[11px] text-red-600 mt-1 pl-4">Informe um celular válido com DDD.</div>}
              </label>
              <label className="block">
                <span className="sr-only">Nome completo</span>
                <div className={`flex items-center gap-2 border rounded-full px-4 py-2.5 transition-colors ${errName ? "border-red-400 bg-red-50" : "border-slate-200 bg-slate-50 focus-within:border-[#25D366] focus-within:bg-white"}`}>
                  <User className="w-4 h-4 text-[#25D366] shrink-0" />
                  <input
                    type="text"
                    autoComplete="name"
                    placeholder="Nome completo *"
                    value={name}
                    onChange={(e) => { setName(e.target.value); if (errName) setErrName(false); }}
                    className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400"
                    data-testid="wa-modal-name"
                  />
                </div>
                {errName && <div className="text-[11px] text-red-600 mt-1 pl-4">Informe seu nome completo.</div>}
              </label>
              <button
                type="submit"
                className="w-full mt-2 bg-[#25D366] hover:bg-[#1DA851] active:bg-[#188E45] text-white font-bold rounded-full px-6 py-2.5 inline-flex items-center justify-center gap-2 transition-colors shadow-sm hover:shadow"
                data-testid="wa-modal-submit"
              >
                Enviar <Send className="w-4 h-4" strokeWidth={2.4} />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

/**
 * Fita de convênios — inspirada em vitrines de operadoras, mas com estética
 * PRÓPRIA: chips coloridos em duas fitas rolando em direções opostas (não é
 * um grid estático de logos). Toda interação (chip, CTA fallback) leva ao
 * WhatsApp do consultor em plantão OU, quando ninguém está online, ao número
 * institucional definido em `REACT_APP_SUPPORT_WA`.
 *
 * Sem logos oficiais das operadoras — RAF não é afiliada. Usamos apenas os
 * nomes em chips com um accent de cor por marca, o que é legalmente seguro
 * e reforça que o objetivo é apenas consultar cobertura via consultor.
 */
const HEALTH_PLANS = [
  { name: "Unimed", accent: "#0A6E31" },
  { name: "Amil One", accent: "#005CAB" },
  { name: "Bradesco Saúde", accent: "#C8102E" },
  { name: "SulAmérica", accent: "#F4801F" },
  { name: "Porto Saúde", accent: "#0058A3" },
  { name: "Cassi", accent: "#F5C542" },
  { name: "Mediservice", accent: "#7BC142" },
  { name: "Santa Helena", accent: "#B22234" },
  { name: "Vivest", accent: "#7A2E8E" },
  { name: "Notre Dame", accent: "#0F5F94" },
  { name: "Hapvida", accent: "#EA8C00" },
  { name: "Golden Cross", accent: "#B58900" },
  { name: "Particular", accent: "#0B2B62" },
];

const HealthPlansStrip = () => {
  const [onCall, setOnCall] = useState(null);
  useEffect(() => {
    api.get("/public/on-call").then((r) => setOnCall(r.data)).catch(() => {});
  }, []);
  const online = !!(onCall && onCall.online);
  const targetPhone = digits(online ? (onCall.phone || SUPPORT_WA) : SUPPORT_WA);
  const attendantName = online && onCall.first_name ? onCall.first_name : "consultor RAF";

  const openWa = (plan) => {
    const planLabel = plan === "geral"
      ? "Olá! Preciso de ajuda com uma internação por dependência química."
      : `Olá, ${attendantName}! Sou família e meu convênio é ${plan}. Gostaria de consultar cobertura para internação/tratamento na rede RAF Direct.`;
    const url = `https://wa.me/${targetPhone}?text=${encodeURIComponent(planLabel)}`;
    trackWhatsApp("home_health_plan", { plan });
    window.open(url, "_blank", "noopener");
  };

  return (
    <section
      className="relative bg-slate-50 border-y border-slate-200"
      data-testid="home-health-plans"
    >
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          {/* Coluna esquerda — texto institucional */}
          <div className="lg:col-span-4 lg:sticky lg:top-24">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-bold">
              <Stethoscope className="w-3.5 h-3.5" /> Convênio e particular
            </div>
            <h2 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight mt-3 text-balance">
              Seu plano cobre? <span className="text-emerald-700">Verificamos em minutos.</span>
            </h2>
            <p className="text-sm text-slate-600 mt-4 leading-relaxed">
              Toque na operadora do seu plano — abrimos uma conversa direta com{" "}
              <b>{online ? `${attendantName}, consultor RAF em plantão agora` : "o atendimento RAF 24h"}</b>.
              Consultamos elegibilidade, unidade parceira e disponibilidade de leito na hora, sem burocracia.
            </p>
            <button
              onClick={() => openWa("geral")}
              data-testid="health-plans-general-cta"
              className="mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-bold text-sm transition-colors"
            >
              <MessageCircle className="w-4 h-4" /> Meu plano não está na lista
            </button>
          </div>

          {/* Coluna direita — grid estático de operadoras */}
          <div className="lg:col-span-8">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {HEALTH_PLANS.map((p) => (
                <button
                  key={p.name}
                  onClick={() => openWa(p.name)}
                  data-testid={`health-plan-chip-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative flex items-center gap-3 px-3 py-3.5 rounded-lg bg-white border border-slate-200 hover:border-[#0B2B62] hover:shadow-md transition-all text-left"
                  title={`Consultar cobertura ${p.name} via WhatsApp`}
                >
                  <span
                    className="w-9 h-9 rounded-md flex items-center justify-center text-[11px] font-black tracking-tight shrink-0 border-2 text-slate-900"
                    style={{ backgroundColor: `${p.accent}1A`, borderColor: p.accent }}
                    aria-hidden
                  >
                    {p.name.replace(/\s+/g, "").slice(0, 2).toUpperCase()}
                  </span>
                  <span className="flex-1 min-w-0 text-sm font-semibold text-slate-800 group-hover:text-[#0B2B62] leading-tight">
                    {p.name}
                  </span>
                  <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-[#0B2B62] transition-colors shrink-0" strokeWidth={2} />
                </button>
              ))}
            </div>

            {/* Disclaimer institucional — bloco tratado, não texto solto */}
            <div className="mt-6 flex items-start gap-3 p-4 rounded-lg bg-white border border-slate-200 text-[12px] text-slate-500 leading-relaxed" data-testid="health-plans-disclaimer">
              <ShieldCheck className="w-4 h-4 text-slate-400 shrink-0 mt-0.5" strokeWidth={2} />
              <p>
                A menção às operadoras <b>não implica vínculo, afiliação ou garantia de cobertura</b> pela RAF Direct.
                A elegibilidade final depende do contrato do beneficiário, indicação clínica e disponibilidade da unidade.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Seção de avaliações reais. Aparece SOMENTE quando `total > 0` — evita
 * mostrar "0 famílias avaliaram" enquanto a rede engatinha. Preservamos
 * "Vozes da rede" (fake) acima, conforme decisão do produto.
 */
const RealReviewsSection = () => {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  useEffect(() => {
    api.get("/public/reviews", { params: { limit: 12 } })
      .then((r) => {
        const items = [...(r.data?.items || [])];
        // Fisher-Yates: em cada page load, ordem embaralha (mais credibilidade)
        for (let i = items.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [items[i], items[j]] = [items[j], items[i]];
        }
        setData({ ...r.data, items });
      })
      .catch(() => {});
  }, []);
  // Grid de 3 por página. Rotação a cada 6s se tiver >3 reviews.
  const perPage = 3;
  const pages = data ? Math.ceil((data.items?.length || 0) / perPage) : 0;
  useEffect(() => {
    if (pages <= 1) return;
    const id = setInterval(() => setPage((p) => (p + 1) % pages), 6000);
    return () => clearInterval(id);
  }, [pages]);
  if (!data || !data.total) return null;
  const start = page * perPage;
  const current = (data.items || []).slice(start, start + perPage);
  return (
    <section className="max-w-7xl mx-auto px-6 py-14" data-testid="home-real-reviews">
      <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl tracking-tight" data-testid="reviews-heading">
            Avaliações RAF Direct
          </h2>
          <div className="mt-3 inline-flex items-center gap-1.5 text-emerald-700 font-bold text-base" data-testid="reviews-count">
            <Star className="w-4 h-4 text-emerald-700" fill="currentColor" />
            {data.total} {data.total === 1 ? "Avaliação" : "Avaliações"}
            {typeof data.avg === "number" && <span className="ml-1 text-slate-600 font-normal">· média {data.avg}</span>}
          </div>
        </div>
        {pages > 1 && (
          <div className="flex gap-1.5" data-testid="reviews-pagination">
            {Array.from({ length: pages }).map((_, i) => (
              <button key={i} onClick={() => setPage(i)} aria-label={`Página ${i+1}`} data-testid={`reviews-dot-${i}`}
                className={`w-2 h-2 rounded-full transition-all ${i === page ? "bg-emerald-600 w-6" : "bg-slate-300 hover:bg-slate-400"}`} />
            ))}
          </div>
        )}
      </div>
      {current.length > 0 && (
        <div key={page} className="grid md:grid-cols-3 gap-4 animate-in fade-in duration-500">
          {current.map((r) => (
            <figure key={r.id} data-testid={`real-review-${r.id}`}
              className="relative p-5 rounded-2xl border border-slate-200 bg-white hover:border-emerald-300 transition-colors">
              <div className="flex gap-0.5 mb-2">
                {[1,2,3,4,5].map((n) => (
                  <Star key={n} className={`w-3.5 h-3.5 ${n <= r.stars ? "text-amber-400" : "text-slate-200"}`} fill={n <= r.stars ? "currentColor" : "none"} />
                ))}
              </div>
              {r.comment && (
                <blockquote className="text-sm text-slate-700 leading-relaxed line-clamp-6">"{r.comment}"</blockquote>
              )}
              <figcaption className="mt-4 pt-3 border-t border-slate-100">
                <div className="font-display font-bold text-[#0B2B62] text-sm">{r.family_display || "Família"}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {r.city || "—"}{r.city && <> · <span className="text-slate-500">Atendimento RAF Direct</span></>}
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
};

const Home = () => {
  usePageTitle(
    null,
    "Plataforma de acolhimento, orientação e direcionamento familiar. Atendimento humano 24h e rede credenciada em saúde mental e bem-estar."
  );
  const { user } = useAuth();
  const nav = useNavigate();
  const gate = (path) => () => {
    if (!user) nav("/entrar", { state: { from: { pathname: path } } });
    else nav(path);
  };

  return (
    <div>
      {/* Banner de topo — full-bleed.
          <picture> com srcset serve versão menor no mobile (~30KB em 640px)
          e maior no desktop, economizando payload sem perder qualidade.
          aspect-ratio reserva o espaço; fetchpriority prioriza o LCP. */}
      <section className="w-full bg-white" style={{ aspectRatio: "1942 / 809" }}>
        <img
          src="/raf-banner-wide-1440.webp"
          srcSet="/raf-banner-wide-640.webp 640w, /raf-banner-wide-960.webp 960w, /raf-banner-wide-1440.webp 1440w, /raf-banner-wide-1942.webp 1942w"
          sizes="100vw"
          alt="RAF Direct — Rede de Apoio à Família"
          className="w-full h-full block object-cover"
          width="1942"
          height="809"
          fetchpriority="high"
          decoding="async"
        />
      </section>

      {/* Hero */}
      <section className="relative border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid md:grid-cols-12 gap-10 items-start md:items-stretch">
          <div className="md:col-span-7 text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-slate-500 mb-5">
              <Sparkles className="w-3.5 h-3.5" /> Plataforma RAF Direct
            </div>
            <h1 className="font-display font-bold text-[#0B2B62] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-balance">
              Acolhimento inteligente.<br />
              <span className="text-[#0F4C81]">Direcionamento seguro.</span>
            </h1>
            <p className="mt-6 text-slate-600 text-base sm:text-lg max-w-xl leading-relaxed mx-auto md:mx-0">
              O caminho mais rápido para transformar uma situação de crise em uma internação
              organizada, segura e documentada. Do primeiro contato à admissão na clínica —
              em um só lugar.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center md:justify-start">
              <button
                data-testid="hero-help-now"
                onClick={gate("/pedir-ajuda")}
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#DC2626] text-white rounded-md font-semibold hover:bg-red-700 transition-colors"
              >
                Preciso de ajuda agora <ArrowRight className="w-4 h-4" />
              </button>
              <button
                data-testid="hero-consult"
                onClick={gate("/meus-atendimentos")}
                className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 text-slate-700 rounded-md font-semibold hover:bg-slate-50 transition-colors"
              >
                Consultar meu atendimento
              </button>
            </div>
          </div>
          <div className="md:col-span-5 flex">
            <div className="flex flex-col w-full gap-4 h-full">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm bg-white flex items-center justify-center aspect-square md:aspect-auto md:flex-1">
                <img
                  src="/raf-atendente.webp"
                  alt="RAF Direct — Atendimento online 24h"
                  className="w-full h-full object-contain"
                  width="1254"
                  height="1254"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="font-display font-bold text-2xl text-[#0B2B62]">&lt; 1 min</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1">Início da IA</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="font-display font-bold text-2xl text-[#0B2B62]">24h</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1">Cobertura</div>
                </div>
                <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
                  <div className="font-display font-bold text-2xl text-[#0B2B62]">LGPD</div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-1">Conformidade</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Destaques — clínicas e profissionais Ouro (carrossel auto-rotativo).
          Não renderiza nada se não houver Ouro cadastrado. */}
      <FeaturedCarousel kind="clinicas" />
      <FeaturedCarousel kind="profissionais" />

      {/* 4 opções principais - bento */}
      <section className="max-w-7xl mx-auto px-6 py-16 text-center md:text-left">
        <div className="mb-8 flex items-center md:items-end justify-center md:justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Escolha como podemos ajudar</div>
            <h2 className="font-display font-bold text-[#0B2B62] text-3xl md:text-4xl mt-2 tracking-tight">Quatro caminhos. Uma missão.</h2>
          </div>
          <div className="text-sm text-slate-500 max-w-md mx-auto md:mx-0">
            Cada ação abaixo inicia um atendimento único, com histórico documentado do primeiro contato até a confirmação da internação.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          {/* Ajuda agora - dominante */}
          <button
            data-testid="btn-help-now"
            onClick={gate("/pedir-ajuda")}
            className="md:col-span-4 md:row-span-2 group text-left p-8 rounded-xl border-2 border-red-200 bg-gradient-to-br from-red-50 to-white hover:border-red-500 transition-colors relative overflow-hidden"
          >
            <div className="flex items-center gap-2 text-red-600 text-xs uppercase tracking-widest font-semibold">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" /> Emergência
            </div>
            <div className="mt-6 flex items-start justify-between">
              <div>
                <div className="w-14 h-14 rounded-xl bg-red-600 text-white flex items-center justify-center mb-5">
                  <AlertCircle className="w-7 h-7" strokeWidth={1.8} />
                </div>
                <h3 className="font-display font-bold text-3xl md:text-4xl text-[#0B2B62] tracking-tight">Preciso de ajuda agora</h3>
                <p className="mt-3 text-slate-600 max-w-md">Triagem inteligente conduzida por IA em minutos. Consultor RAF em contato logo depois.</p>
              </div>
              <ArrowRight className="w-6 h-6 text-red-600 mt-2 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </button>

          {/* Avaliação */}
          <button
            data-testid="btn-evaluation"
            onClick={gate("/pedir-ajuda")}
            className="md:col-span-2 group text-left p-6 rounded-xl border border-slate-200 bg-blue-50/40 hover:border-blue-400 transition-colors"
          >
            <div className="w-11 h-11 rounded-lg bg-blue-600 text-white flex items-center justify-center mb-4">
              <ClipboardCheck className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <h3 className="font-display font-bold text-xl text-[#0B2B62]">Quero uma avaliação</h3>
            <p className="mt-2 text-sm text-slate-600">Entenda o quadro sem pressa. A IA constrói um dossiê para você.</p>
          </button>

          {/* Clínicas */}
          <button
            data-testid="btn-search-clinics"
            onClick={gate("/clinicas")}
            className="md:col-span-2 group text-left p-6 rounded-xl border border-slate-200 bg-orange-50/40 hover:border-orange-400 transition-colors"
          >
            <div className="w-11 h-11 rounded-lg bg-orange-600 text-white flex items-center justify-center mb-4">
              <Building2 className="w-5 h-5" strokeWidth={1.8} />
            </div>
            <h3 className="font-display font-bold text-xl text-[#0B2B62]">Procurar uma clínica</h3>
            <p className="mt-2 text-sm text-slate-600">Explore clínicas parceiras com filtros por perfil, cidade e modalidade.</p>
          </button>

          {/* Consultar */}
          <button
            data-testid="btn-consult-case"
            onClick={gate("/meus-atendimentos")}
            className="md:col-span-6 group text-left p-6 rounded-xl border border-slate-200 bg-white hover:border-slate-400 transition-colors flex items-center justify-between"
          >
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center">
                <FileSearch className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <h3 className="font-display font-bold text-xl text-[#0B2B62]">Consultar meu atendimento</h3>
                <p className="text-sm text-slate-600 mt-0.5">Acompanhe a linha do caso, envie documentos e converse com o consultor.</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-400 group-hover:text-slate-700 transition-colors" />
          </button>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-8 text-center md:text-left">
          {[
            { icon: Sparkles, t: "Triagem em minutos", d: "IA estruturada no protocolo do RAF Direct, adaptando perguntas conforme respostas." },
            { icon: HeartHandshake, t: "Consultor humano", d: "Após a análise, um consultor RAF assume o caso e conduz a família com empatia." },
            { icon: ShieldCheck, t: "Processo documentado", d: "Dossiê Inteligente RAF, timeline, chat e reserva — tudo em um único atendimento." },
          ].map((f) => (
            <div key={f.t} className="p-6 rounded-xl bg-white border border-slate-200 flex flex-col items-center md:items-start">
              <f.icon className="w-6 h-6 text-[#0B2B62]" strokeWidth={1.8} />
              <h3 className="font-display font-semibold text-xl text-[#0B2B62] mt-4">{f.t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fita de convênios — CTA rápido pra WhatsApp do consultor em plantão */}
      <HealthPlansStrip />

      {/* Vitrine de Impacto — CTA para a página pública /impacto */}
      <section className="bg-gradient-to-br from-[#0B2B62] to-[#1e4d9e] text-white" data-testid="home-impact-cta">
        <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-7">
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-blue-200 mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Vitrine de Impacto
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl leading-tight">
              Não pedimos confiança. Nós <span className="text-blue-200">provamos com dados.</span>
            </h2>
            <p className="mt-4 text-blue-100 text-base sm:text-lg leading-relaxed max-w-xl">
              Famílias atendidas, tempo médio até internação, estados alcançados — cada número na página de impacto
              é reconstruível pelo audit log da plataforma. Transparência é matéria-prima da rede RAF.
            </p>
          </div>
          <div className="md:col-span-5 flex flex-col gap-3">
            <Link to="/impacto" data-testid="home-impact-btn"
              className="inline-flex items-center justify-between gap-3 px-5 py-4 bg-white text-[#0B2B62] rounded-xl font-bold hover:bg-slate-100 transition-colors">
              <span>Ver números da rede em tempo real</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/ranking" data-testid="home-ranking-btn"
              className="inline-flex items-center justify-between gap-3 px-5 py-4 border-2 border-white/40 text-white rounded-xl font-semibold hover:bg-white/10 transition-colors">
              <span>Ver ranking transparente</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <RealReviewsSection />
      <OnCallBadge />
      <HomeNudge />
    </div>
  );
};

export default Home;
